// App.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate, Outlet } from 'react-router-dom';
import { Box, Typography, IconButton } from '@mui/material';
import { MenuRounded as MenuRoundedIcon,} from '@mui/icons-material';
import SideNav from './components/SideNav';
import Home from './Home';
import { AuthContext, AuthProvider } from './auth/AuthContext';
import { ThemeProvider } from './components/ThemeProvider';
import { useImpersonateDesignFirm } from './ImpersonateDesignFirm';
import DesignFirmsManager from './components/DesignFirmManager';
import { Teams } from './components/Teams';
import { Billing } from './components/Billing';
import { firebaseHelper, usePermissions } from 'ui';
import { Permissions } from 'ts-interfaces';
import { Settings } from './components/Settings';
import { TeamCredits } from './components/TeamCredits';

// Main Layout Component
const Layout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [toggled, setToggled] = useState(false);
  const location = useLocation();
  const isRootPath = location.pathname === '/' || location.pathname === '/not_allowed';

  const handleCollapsedChange = (value) => {
    setCollapsed(value);
  };

  const handleToggleSidebar = (value) => {
    setToggled(value);
  };

  return (
      <Box sx={{ display: 'flex', height: '100vh', position: 'relative'}} >
        {!isRootPath && (
            <SideNav open={collapsed}  onClose= { () => {   }} />
        )}
        
        <Box sx={{flex: 1,display: 'flex',flexDirection: 'column',position: 'relative',}} >
          {!isRootPath && (
            <Box sx={{padding: '8px 16px',display: { xs: 'flex', md: 'none' },alignItems: 'center',}}>
              <IconButton onClick={() => handleToggleSidebar(true)}>
                <MenuRoundedIcon />
              </IconButton>
            </Box>
          )}          
          <Box sx={{ flex: 1, overflow: 'auto', backgroundColor:'background.default'  } } >
            {children}
          </Box>
        </Box>
      </Box>
  );
};



function SwitchApp() {
  const {currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const impersonateDesignFirm = useImpersonateDesignFirm( (state) => state.impersonate_firm);
  const { permissions, loading, error }: { permissions: Permissions | null; loading: boolean; error: Error | null } = usePermissions(currentUser?.uid, firebaseHelper.db);

  const isAdmin = currentUser &&  permissions?.teamsAdmin && permissions.teamsAdmin.length > 0

  const currentDesignFirmID = useMemo(() => {
    if (permissions?.teamsSuperuser===true && impersonateDesignFirm) {
      return impersonateDesignFirm.id;
    }
    else if (isAdmin) { 
      return permissions.teamsAdmin[0]
    }
    else return null; 

  },[currentUser,permissions,impersonateDesignFirm]);

  useEffect(() => { 
    if (loading) return;
    const isAllowed = isAdmin || permissions?.teamsSuperuser===true
    if (currentUser==null) {   navigate('/'); }
    else if (currentUser && isAllowed === false) { navigate('/not_allowed'); }
    else if (permissions?.teamsSuperuser===true  && impersonateDesignFirm == null) { navigate('/pr/firms'); }
    else if (isAllowed ===true &&  (!location.pathname.startsWith('pr')) ) { navigate('/pr/teams'); }

  },[permissions,currentUser,impersonateDesignFirm,loading]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }
  return (
    <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/not_allowed" element={<Home noaccess={true} />} />

        <Route element={<ProtectedRoute isAllowed={isAdmin || permissions?.teamsSuperuser===true }  children={undefined} />}>
          <Route path="/pr/firms" element={<DesignFirmsManager />} />
          <Route path="/pr/teams" element={<Teams designFirmID={currentDesignFirmID} />} />
          <Route path="/pr/billing" element={<Billing designFirmID={currentDesignFirmID}  />} />
          <Route path="/pr/credits" element={<TeamCredits designFirmID={currentDesignFirmID} />} />
          <Route path="/pr/settings" element={<Settings designFirmID={currentDesignFirmID} />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
const App = () => {
  return (
    <AuthProvider>
    <ThemeProvider>
    <BrowserRouter>
      <Layout>
        <SwitchApp />
      </Layout>
    </BrowserRouter>
    </ThemeProvider>
    </AuthProvider>
  );
};

const ProtectedRoute = ({ isAllowed, children }) => {
  if (!isAllowed) { return <Navigate to={'/not_allowed'} replace />; }
  else {
    return children ? children : <Outlet />;
  }
};

export default App;